import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Layout, Row, Col, Card, Typography, message, Drawer, Button, Space, Tooltip } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { selectAppPermissions } from "../../../store/auth/selectors";
import {
  getActasPO, getStatsEntregasPO, getLineasActaPO, createEntregaPO, deleteEntregaPO
} from "../../../services/financiero/ingresosPO/entregasPOService";
import {
  getParticipantesPO, getOrganizacionesPO, getMunicipiosPO, getDepartamentosPO,
  getProyectosPO, getResponsablesPO
} from "../../../services/financiero/ingresosPO/catalogosPOService";
import { getOrdenesServicioPO } from "../../../services/financiero/ingresosPO/ordenesServicioPOService";
import { getBodegas } from "../../../services/financiero/ingresosPO/bodegasPOService";
import Header160 from "../../header/header";
import styles from "../ingresosPOStyles.module.css";
import EntregasPOCards from "./components/EntregasPOCards";
import EntregasPOFilters from "./components/EntregasPOFilters";
import EntregasPOTable from "./components/EntregasPOTable";
import EntregaActaForm from "./components/EntregaActaForm";

const { Content } = Layout;
const { Title, Text } = Typography;

export default function EntregasPOPage() {

  const appName = "entregas_ingresos_po";
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const user = useSelector((state) => state.auth.user);
  const userLogin = user?.username || "system";
  const permissionAccess = permissions?.priv_access === "Y";
  const permissionDelete = permissions?.priv_delete === "Y";
  const permissionInsert = permissions?.priv_insert === "Y";

  const [loading, setLoading] = useState(false);
  const [actas, setActas] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filtros, setFiltros] = useState({});

  const [participantes, setParticipantes] = useState([]);
  const [organizaciones, setOrganizaciones] = useState([]);
  const [productos, setProductos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [catalogosListos, setCatalogosListos] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [validation, setValidation] = useState({ isValid: false });
  const [saving, setSaving] = useState(false);

  const cargarActas = async (page = pagination.current, pageSize = pagination.pageSize, filtrosActuales = filtros) => {
    try {
      setLoading(true);
      const res = await getActasPO({ page, limit: pageSize, ...filtrosActuales });
      setActas(res.data || []);
      setPagination({ current: res.page, pageSize: res.limit, total: res.total });
    } catch (error) {
      console.error(error);
      message.error("Error cargando entregas");
    } finally {
      setLoading(false);
    }
  };

  const cargarStats = async (filtrosActuales = filtros) => {
    try {
      const res = await getStatsEntregasPO(filtrosActuales);
      setStats(res);
    } catch (error) {
      console.error(error);
    }
  };

  const cargarCatalogos = async () => {
    try {
      const [part, org, prod, mun, dep, proy, bod, resp] = await Promise.all([
        getParticipantesPO(),
        getOrganizacionesPO(),
        getOrdenesServicioPO(),
        getMunicipiosPO(),
        getDepartamentosPO(),
        getProyectosPO(),
        getBodegas(),
        getResponsablesPO()
      ]);
      setParticipantes(part || []);
      setOrganizaciones(org || []);
      setProductos(prod || []);
      setMunicipios(mun || []);
      setDepartamentos(dep || []);
      setProyectos(proy || []);
      setBodegas(bod || []);
      setResponsables(resp || []);
    } catch (error) {
      console.error(error);
      message.error("Error cargando catálogos");
    } finally {
      setCatalogosListos(true);
    }
  };

  useEffect(() => {
    if (permissionAccess) {
      cargarActas(1, pagination.pageSize, {});
      cargarStats({});
      cargarCatalogos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionAccess]);

  const handleTableChange = (paginationInfo) => {
    cargarActas(paginationInfo.current, paginationInfo.pageSize, filtros);
  };

  const handleFilter = (nuevosFiltros) => {
    setFiltros(nuevosFiltros);
    cargarActas(1, pagination.pageSize, nuevosFiltros);
    cargarStats(nuevosFiltros);
  };

  const handleRefrescar = () => {
    cargarActas();
    cargarStats(filtros);
  };

  const handleDeleteActa = async (record) => {
    try {
      const lineas = await getLineasActaPO({
        oid_participante: record.oid_participante,
        tipo_entrega: record.tipo_entrega,
        fecha_entrega: record.fecha_entrega,
        oid_bodega: record.oid_bodega
      });
      await Promise.all(lineas.map((l) => deleteEntregaPO(l.oid, userLogin)));
      message.success("Acta eliminada");
      cargarActas();
      cargarStats(filtros);
    } catch (error) {
      console.error(error);
      message.error("Error eliminando la acta");
    }
  };

  const handleGuardarActa = async () => {
    if (!validation.isValid) {
      message.warning("Complete los campos obligatorios y verifique el stock");
      return;
    }
    if (!rows.length) {
      message.warning("Agregue al menos una línea de producto");
      return;
    }
    try {
      setSaving(true);
      const payload = rows.map((r) => ({
        ...r,
        fecha_entrega: r.fecha_entrega?.format ? r.fecha_entrega.format("YYYY-MM-DD") : r.fecha_entrega
      }));
      await Promise.all(payload.map((item) => createEntregaPO(item, userLogin)));
      message.success("Acta de entrega registrada");
      setDrawerOpen(false);
      cargarActas(1, pagination.pageSize, filtros);
      cargarStats(filtros);
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.message || "Error guardando la entrega");
    } finally {
      setSaving(false);
    }
  };

  if (!permissionAccess) {
    return (
      <Content style={{ padding: 24 }}>
        <Card>
          <Text>No tienes permisos para acceder a este módulo.</Text>
        </Card>
      </Content>
    );
  }

  return (
    <div className={`container ${styles.ingresosPoModule}`}>
      <Content style={{ padding: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div className="header-filter-container d-flex align-items-center justify-content-between" style={{ flexWrap: "wrap", gap: 16 }}>
              <Header160 />
              <Col flex="auto" style={{ textAlign: "center", minWidth: 280 }}>
                <Space direction="vertical" size={2} align="center">
                  <Title level={4} style={{ margin: 0, color: "#042956", fontWeight: 600 }}>
                    Entregas — Ingresos PO
                  </Title>
                  <Space wrap size={8} style={{ marginTop: 8 }}>
                    {permissionInsert && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawerOpen(true)}>
                        Nueva entrega
                      </Button>
                    )}
                    <Tooltip title="Refrescar">
                      <Button icon={<ReloadOutlined />} onClick={handleRefrescar} />
                    </Tooltip>
                  </Space>
                </Space>
              </Col>
            </div>
          </Col>
          <Col span={24}>
            <EntregasPOCards stats={stats} />
          </Col>
          <Col span={24}>
            <EntregasPOFilters
              proyectos={proyectos}
              departamentos={departamentos}
              municipios={municipios}
              bodegas={bodegas}
              onFilter={handleFilter}
            />
          </Col>
          <Col span={24}>
            <EntregasPOTable
              data={actas}
              pagination={pagination}
              onTableChange={handleTableChange}
              loading={loading}
              onDeleteActa={handleDeleteActa}
              permissionDelete={permissionDelete}
            />
          </Col>
        </Row>

        <Drawer
          title="Nueva acta de entrega"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={1200}
          destroyOnClose
          extra={
            <Button type="primary" loading={saving} onClick={handleGuardarActa}>
              Guardar acta
            </Button>
          }
        >
          {catalogosListos ? (
            <EntregaActaForm
              participantes={participantes}
              organizaciones={organizaciones}
              productos={productos}
              municipios={municipios}
              departamentos={departamentos}
              proyectos={proyectos}
              bodegas={bodegas}
              responsables={responsables}
              onChange={setRows}
              onValidationChange={setValidation}
            />
          ) : (
            <div style={{ textAlign: "center", padding: 60 }}>Cargando catálogos…</div>
          )}
        </Drawer>
      </Content>
    </div>
  );
}
